import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, XCircle, Plus, Upload, Download, X, AlertTriangle, Pencil, Check, Trash2 } from 'lucide-react';
import { type Position, type Quote } from '@invest-copilot/shared';
import { fetchJSON } from '../lib/api-client';
import { feeModelFor, netPnlPctNoShares, roundTripPnl } from '../lib/fees';
import { PORTFOLIO_KEY, emitPortfolioChange } from '../lib/usePortfolio';
import { resolveSymbolInput } from '../lib/resolveSymbol';
import { BUYING_POWER_STORAGE_KEY, BUYING_POWER_CHANGE_EVENT, loadBuyingPowerSettings, type StrategyProfile } from './BuyingPowerSettings';
import type { Alert } from '../lib/notifications';

type ThesisStatus = 'pending-fill' | 'open' | 'stopped' | 't1_hit' | 't2_hit' | 'thesis_broken' | 'manually_closed' | 'expired';

interface Fill {
  id: string;
  side: 'buy' | 'sell';
  price: number;
  quantity: number;
  filledAt: string;
  currency: 'USD' | 'TWD';
  fees?: number;
  feesIncluded?: boolean;
}

interface Thesis {
  id: string;
  symbol: string;
  status: ThesisStatus;
  adoptedAt: string;
  closedAt?: string;
  entry: number;
  stop: number;
  t1: number;
  t2: number;
  holdDaysExpected?: [number, number];
  expectedReturn?: [number, number];
  initialConfidence?: number;
  reasonsTop3?: string[];
  failureConditions?: string[];
  finalPnlPct?: number;
  finalReason?: string;
  source?: string;
  broker?: 'fubon' | 'cathay';
  actualEntry?: number;
  actualExit?: number;
  sharesPlanned?: number;
  filledShares?: number;
  remainingShares?: number;
  realizedPnl?: number;
  realizedPnlPct?: number;
  /** 損平價：部分賣出後把已實現損益攤進剩餘部位 ＝ 賣到此價整筆打平；只在有賣出且有剩餘時有值。 */
  breakEvenPrice?: number;
  fills?: Fill[];
  evaluations?: Array<{ date: string; quote: number; pnlPct: number; statusAtDate: ThesisStatus }>;
  trailingStop?: { method: string; current: number; lastUpdatedAt: string };
}

interface ThesesResponse { theses: Thesis[]; count: number }
interface PendingAlertsResponse { alerts: Alert[]; count: number; unreadCount?: number; includeRead?: boolean }

const THESIS_ALERT_TYPES = ['score-flipped-bearish', 'score-flipped-bullish', 'failure-condition-triggered', 'stop-hit', 'thesis-broken'] as const;

function isClosedStatus(status: ThesisStatus): boolean {
  return status === 'stopped' || status === 'thesis_broken' || status === 'manually_closed' || status === 'expired';
}

function currencyFor(symbol: string): 'USD' | 'TWD' {
  return symbol.endsWith('.TW') || symbol.endsWith('.TWO') ? 'TWD' : 'USD';
}

/** 富邦匯入部位 actualEntry(=costPrice) 已含買費 → 未實現損益買費不重估（賣費仍估）。 */
function feeOpts(t: Thesis): { buyFeeIncluded: boolean } | undefined {
  const inc = t.broker === 'fubon' || (t.fills ?? []).some((f) => f.side === 'buy' && f.feesIncluded);
  return inc ? { buyFeeIncluded: true } : undefined;
}

function uid(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

function loadLocalPortfolio(): Position[] {
  try {
    const raw = localStorage.getItem(PORTFOLIO_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as Position[]) : [];
  } catch {
    return [];
  }
}

function saveLocalPortfolio(positions: Position[]): void {
  try {
    localStorage.setItem(PORTFOLIO_KEY, JSON.stringify(positions));
    // 通知 useMyPosition（K 線「我的成本」線）/ usePortfolioSymbols（候選池）同步。
    emitPortfolioChange();
  } catch {/**/}
}

/** 刪除交易計畫時同步移除本機 portfolio 該 symbol（讓 K 線成本線 / 候選池不再残留）。 */
function removeLocalPortfolioSymbol(symbol: string): void {
  const next = loadLocalPortfolio().filter((p) => p.symbol.toUpperCase() !== symbol.toUpperCase());
  saveLocalPortfolio(next);
}

/** Position → /api/thesis/import body（帳戶幣別由 symbol 後綴決定，不換匯）。 */
function toImportBody(p: Position) {
  return {
    symbol: p.symbol,
    market: p.market === 'TW' ? 'tw' : 'us',
    shares: p.qty,
    avgCost: p.costBasis,
    entryDate: p.buyDate,
    source: 'manual',
    notes: p.note,
  };
}

function money(n: number | undefined, symbol: string): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  const currency = currencyFor(symbol);
  return `${currency} ${n.toLocaleString(undefined, { maximumFractionDigits: currency === 'TWD' ? 0 : 2 })}`;
}

function price(n: number | undefined): string {
  return typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 4 }) : '—';
}

function pct(n: number | undefined): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return `${n >= 0 ? '+' : ''}${(n * 100).toFixed(2)}%`;
}

function sharesOf(thesis: Thesis): number | undefined {
  const s = thesis.remainingShares ?? thesis.filledShares ?? thesis.sharesPlanned;
  return typeof s === 'number' && s > 0 ? s : undefined;
}

/** 帳戶幣別不換匯：USD / TWD 各自一行（見 copilot-instructions §3.5） */
function moneyByCurrency(usd: number, twd: number, hasUsd: boolean, hasTwd: boolean): string {
  const parts: string[] = [];
  if (hasUsd) parts.push(`USD ${usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
  if (hasTwd) parts.push(`TWD ${twd.toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
  return parts.length ? parts.join(' · ') : '—';
}

/** 即時匯率換算成「另一個幣別」的金額字串，方便抓到不小心用錯帳戶幣別買的部位。 */
function convertedMoney(amount: number, from: 'USD' | 'TWD', usdTwd: number | undefined): string | null {
  if (typeof usdTwd !== 'number' || !(usdTwd > 0)) return null;
  if (from === 'USD') {
    return `≈ TWD ${(amount * usdTwd).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
  return `≈ USD ${(amount / usdTwd).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function statusLabel(status: ThesisStatus): string {
  const map: Record<ThesisStatus, string> = {
    'pending-fill': '待成交',
    open: '持有中',
    stopped: '停損',
    t1_hit: 'T1 達標',
    t2_hit: 'T2 達標',
    thesis_broken: '交易假設破壞',
    manually_closed: '手動關閉',
    expired: '過期',
  };
  return map[status] ?? status;
}

function thesisSourceLabel(source: string): string {
  const map: Record<string, string> = {
    'decision-engine': '引擎決策',
    'adopted-decision': '採納決策',
    manual: '手動',
    imported: '匯入',
  };
  return map[source] ?? source;
}

function alertLabel(type: string): string {
  const map: Record<string, string> = {
    'score-flipped-bearish': '訊號翻空',
    'score-flipped-bullish': '訊號翻多候選',
    'failure-condition-triggered': '失敗條件警示',
    'stop-hit': '停損觸發',
    'thesis-broken': '交易假設破壞',
  };
  return map[type] ?? type;
}

function alertBannerClass(severity: Alert['severity']): string {
  if (severity === 'critical') return 'border-(--down)/60 bg-(--down)/15 text-(--down)';
  if (severity === 'warning') return 'border-(--warn)/60 bg-(--warn)/15 text-(--warn)';
  return 'border-(--accent)/50 bg-(--accent)/8 text-(--accent)';
}

function latestAlertsForThesis(thesis: Thesis, alerts: Alert[]): Alert[] {
  const today = new Date().toISOString().slice(0, 10);
  return alerts
    .filter((alert) => alert.thesisId ? alert.thesisId === thesis.id : alert.symbol === thesis.symbol)
    // 未讀一律顯示；已讀只顯示「今天建立」的 —— 避免上週五已讀的停損 alert 一直掛在卡上「每天跳」。
    .filter((alert) => !alert.readAt || alert.createdAt.slice(0, 10) === today)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .slice(0, 2);
}

function latestQuote(thesis: Thesis): number | undefined {
  const latest = thesis.evaluations?.[thesis.evaluations.length - 1];
  return latest?.quote ?? thesis.actualExit ?? thesis.actualEntry ?? thesis.entry;
}

/** L1 因子內部欄位名 → 給使用者看的中文短標（facts-only reasonsTop3 會帶英文欄位名）。 */
const L1_FACTOR_LABELS: Record<string, string> = {
  priceVsPrevClose: '昨收比較',
  priceVsMA20: '20 日線',
  priceVsMA60: '60 日線',
  macdHist: 'MACD',
  rsiZone: 'RSI',
  volBoost: '量能',
};

/**
 * 解析 facts-only 後端組的 reason 字串 `L1 <factorName>：±N (note)`（見 lib/facts-only-decision.ts），
 * 拆成 { label, note, contribution } 給前端做漂亮的 chip。非此格式（LLM 句子）回 null。
 */
function parseL1Reason(text: string): { label: string; note: string; contribution: number } | null {
  const m = /^L1\s+([A-Za-z0-9_]+)\s*[：:]\s*([+-]?\d+(?:\.\d+)?)\s*(?:\(([^)]*)\))?\s*$/.exec(text.trim());
  if (!m) return null;
  const [, name = '', contrib = '0', note] = m;
  return { label: L1_FACTOR_LABELS[name] ?? name, note: (note ?? '').trim(), contribution: Number(contrib) };
}

export function MyThesesPanel({ onSelectSymbol }: { onSelectSymbol?: (symbol: string) => void }) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'open' | 'closed'>('open');
  const [adding, setAdding] = useState(false);
  const [importText, setImportText] = useState<string | null>(null);
  const [ioMsg, setIoMsg] = useState<string | null>(null);
  // 交易風格（購買力設定改）決定要不要顯示風險指標：短線 day-swing 隱藏。
  const [strategyProfile, setStrategyProfile] = useState<StrategyProfile>(() => loadBuyingPowerSettings().strategyProfile);
  useEffect(() => {
    const refresh = (e?: Event) => {
      if (e instanceof StorageEvent && e.key && e.key !== BUYING_POWER_STORAGE_KEY) return;
      setStrategyProfile(loadBuyingPowerSettings().strategyProfile);
    };
    window.addEventListener(BUYING_POWER_CHANGE_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(BUYING_POWER_CHANGE_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);
  const theses = useQuery({
    queryKey: ['theses', tab, 365],
    queryFn: () => fetchJSON<ThesesResponse>(`/api/theses?status=${tab}&days=365`),
    staleTime: 60_000,
    retry: false,
  });
  const thesisAlerts = useQuery({
    queryKey: ['alerts', 'thesis-banners', 7],
    queryFn: () => fetchJSON<PendingAlertsResponse>(`/api/alerts/pending?includeRead=1&days=7&type=${THESIS_ALERT_TYPES.join(',')}`),
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: false,
  });
  const close = useMutation({
    mutationFn: async (id: string) => fetchJSON(`/api/thesis/${encodeURIComponent(id)}/close`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: 'manually_closed' }),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['theses'] });
      queryClient.invalidateQueries({ queryKey: ['daily-plan'] });
      queryClient.invalidateQueries({ queryKey: ['managed-theses'] });
    },
  });

  // 真正刪除（誤建 / 測試資料）：從 Cosmos 移除 thesis + 同步清掉本機 portfolio 該 symbol。
  const del = useMutation({
    mutationFn: async (thesis: Thesis) => {
      await fetchJSON(`/api/thesis/${encodeURIComponent(thesis.id)}/delete`, { method: 'DELETE' });
      removeLocalPortfolioSymbol(thesis.symbol);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['theses'] });
      queryClient.invalidateQueries({ queryKey: ['daily-plan'] });
      queryClient.invalidateQueries({ queryKey: ['managed-theses'] });
    },
  });

  // 新增 / 匯入既有持股：同時寫本機 localStorage（K 線成本線 / 候選池依賴）+ 交給企鵝管理（thesis import）。
  const importHoldings = useMutation({
    mutationFn: async (positions: Position[]) => {
      const withIds = positions.map((p) => ({ ...p, id: p.id || uid() }));
      saveLocalPortfolio([...loadLocalPortfolio(), ...withIds]);
      const [first] = withIds;
      if (first && withIds.length === 1) {
        return fetchJSON('/api/thesis/import', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(toImportBody(first)),
        });
      }
      return fetchJSON('/api/thesis/import/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ holdings: withIds.map(toImportBody) }),
      });
    },
    onSuccess: (_data, positions) => {
      setAdding(false);
      setImportText(null);
      setIoMsg(`🐧 已接手 ${positions.length} 筆持股，出現在下方交易計畫`);
      queryClient.invalidateQueries({ queryKey: ['theses'] });
      queryClient.invalidateQueries({ queryKey: ['daily-plan'] });
    },
    onError: (e) => setIoMsg(`匯入失敗：${e instanceof Error ? e.message : String(e)}`),
  });

  function submitAddHolding(p: Omit<Position, 'id'>) {
    setIoMsg(null);
    importHoldings.mutate([{ ...p, id: uid() }]);
  }

  function applyImportText() {
    if (!importText) return;
    let valid: Position[];
    try {
      const parsed = JSON.parse(importText) as Position[];
      if (!Array.isArray(parsed)) throw new Error('not an array');
      valid = parsed
        .filter((p) => p && p.symbol && typeof p.qty === 'number' && typeof p.costBasis === 'number')
        .map((p) => ({ ...p, id: p.id || uid() }));
    } catch {
      setIoMsg('匯入失敗：JSON 格式錯誤');
      return;
    }
    if (valid.length === 0) { setIoMsg('沒有有效持股可匯入'); return; }
    if (!confirm(`要把 ${valid.length} 筆持股交給企鵝管理嗎？沒有停損/T1/T2 的會呼叫 AI 接手。`)) return;
    importHoldings.mutate(valid);
  }

  function exportHoldings() {
    const text = JSON.stringify(loadLocalPortfolio(), null, 2);
    navigator.clipboard.writeText(text).then(
      () => setIoMsg('已複製本機持股 JSON 到剪貼簿'),
      () => setIoMsg('複製失敗，請手動選取'),
    );
  }
  const rows = theses.data?.theses ?? [];
  const recentAlerts = thesisAlerts.data?.alerts ?? [];

  // 進行中部位即時報價（去重 symbol；react-query 依 queryKey 自動共用）。
  const openSymbols = useMemo(
    () => Array.from(new Set(rows.filter((t) => !isClosedStatus(t.status)).map((t) => t.symbol))),
    [rows],
  );
  const quoteResults = useQueries({
    queries: openSymbols.map((symbol) => ({
      queryKey: ['quote', symbol],
      queryFn: () => fetchJSON<Quote>(`/api/quote/${encodeURIComponent(symbol)}`),
      staleTime: 30_000,
      refetchInterval: 60_000,
      retry: false,
    })),
  });
  const livePrices = useMemo(() => {
    const map = new Map<string, number>();
    openSymbols.forEach((symbol, i) => {
      const p = quoteResults[i]?.data?.price;
      if (typeof p === 'number' && Number.isFinite(p)) map.set(symbol, p);
    });
    return map;
  }, [openSymbols, quoteResults]);

  // 即時 USD→TWD 匯率（Yahoo TWD=X），用來把損益換算到另一個帳戶幣別。
  const fx = useQuery({
    queryKey: ['quote', 'TWD=X'],
    queryFn: () => fetchJSON<Quote>('/api/quote/TWD=X'),
    staleTime: 60_000,
    refetchInterval: 300_000,
    retry: false,
  });
  const usdTwd = typeof fx.data?.price === 'number' && fx.data.price > 0 ? fx.data.price : undefined;

  const stats = useMemo(() => {
    let open = 0;
    let realizedUSD = 0;
    let realizedTWD = 0;
    let realizedUsdSeen = false;
    let realizedTwdSeen = false;
    let unrealUSD = 0;
    let unrealTWD = 0;
    let unrealUsdSeen = false;
    let unrealTwdSeen = false;
    for (const t of rows) {
      const cur = currencyFor(t.symbol);
      if (typeof t.realizedPnl === 'number' && Number.isFinite(t.realizedPnl)) {
        if (cur === 'USD') { realizedUSD += t.realizedPnl; realizedUsdSeen = true; }
        else { realizedTWD += t.realizedPnl; realizedTwdSeen = true; }
      }
      if (!isClosedStatus(t.status)) {
        open += 1;
        const live = livePrices.get(t.symbol);
        const basis = t.actualEntry ?? t.entry;
        const shares = sharesOf(t);
        if (live != null && basis > 0 && shares) {
          const rt = roundTripPnl(basis, live, shares, feeModelFor(t.symbol), feeOpts(t));
          if (rt) {
            if (cur === 'USD') { unrealUSD += rt.netPnl; unrealUsdSeen = true; }
            else { unrealTWD += rt.netPnl; unrealTwdSeen = true; }
          }
        }
      }
    }
    return {
      open,
      realizedUSD, realizedTWD, realizedUsdSeen, realizedTwdSeen,
      unrealUSD, unrealTWD, unrealUsdSeen, unrealTwdSeen,
    };
  }, [rows, livePrices]);

  // 彙總顏色 / 正負以「換匯後合計（TWD 基準）」判斷，跟下方「合計 ≈ TWD」小字一致；
  // 不能直接 USD+TWD 相加（幣別不同、會把 USD 賺 + TWD 賠誤判成綠色）。無匯率時退回原幣相加（單一幣別仍正確）。
  const fxRate = usdTwd ?? 1;
  const realizedTotal = stats.realizedUSD * fxRate + stats.realizedTWD;
  const unrealTotal = stats.unrealUSD * fxRate + stats.unrealTWD;

  // 風險指標（HHI 集中度）只給中長線參考：短線(day-swing)隱藏。
  // 跨幣別用即時匯率正規化到 TWD 再算權重（幣別不換匯原則，僅集中度比較用）。
  const showRisk = strategyProfile !== 'day-swing';
  const risk = useMemo(() => {
    if (!showRisk) return null;
    const values = rows
      .filter((t) => !isClosedStatus(t.status))
      .map((t) => {
        const shares = sharesOf(t) ?? 0;
        const px = livePrices.get(t.symbol) ?? latestQuote(t) ?? t.entry;
        const raw = shares > 0 && px > 0 ? shares * px : 0;
        return currencyFor(t.symbol) === 'USD' ? (usdTwd ? raw * usdTwd : raw) : raw;
      })
      .filter((v) => v > 0);
    const total = values.reduce((s, v) => s + v, 0);
    if (total <= 0 || values.length === 0) return null;
    const hhi = values.reduce((s, v) => s + (v / total) ** 2, 0);
    const maxWeight = Math.max(...values.map((v) => v / total));
    return { hhi, count: values.length, maxWeight };
  }, [showRisk, rows, livePrices, usdTwd]);

  // 帳戶幣別不換匯：台股（.TW/.TWO）與美股各自分區顯示。
  const twRows = rows.filter((t) => currencyFor(t.symbol) === 'TWD');
  const usRows = rows.filter((t) => currencyFor(t.symbol) === 'USD');
  const marketGroups = [
    { key: 'tw', label: '🇹🇼 台股', list: twRows },
    { key: 'us', label: '🇺🇸 美股', list: usRows },
  ].filter((g) => g.list.length > 0);

  return (
    <section className="card mb-3">
      <div className="card-h">
        <h2>📋 我的交易計畫</h2>
        <div className="card-h-r">
          <span className="tag">已採納 / 匯入 · 成交回填才算實際損益</span>
          <div className="seg">
            <button type="button" onClick={() => setTab('open')} className={`seg-btn ${tab === 'open' ? 'active' : ''}`}>進行中</button>
            <button type="button" onClick={() => setTab('closed')} className={`seg-btn ${tab === 'closed' ? 'active' : ''}`}>已結案</button>
          </div>
          <button
            type="button"
            onClick={() => theses.refetch()}
            disabled={theses.isFetching}
            title="重新讀取交易計畫"
            aria-label="重新讀取交易計畫"
            className="inline-flex items-center gap-1 rounded border border-(--border) px-2 py-1 text-[11px] text-(--text-secondary) hover:border-(--accent) hover:text-(--accent) disabled:opacity-50"
          >
            <RefreshCw size={12} className={theses.isFetching ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => { setAdding((v) => !v); setImportText(null); setIoMsg(null); }}
          className="inline-flex items-center gap-1 rounded border border-(--accent)/50 px-2 py-1 text-[11px] font-semibold text-(--accent) hover:bg-(--accent)/10"
        >
          <Plus size={12} /> 新增持股
        </button>
        <button
          type="button"
          onClick={() => { setImportText((v) => (v === null ? '' : null)); setAdding(false); setIoMsg(null); }}
          className="inline-flex items-center gap-1 rounded border border-(--border) px-2 py-1 text-[11px] text-(--text-secondary) hover:border-(--accent) hover:text-(--accent)"
        >
          <Upload size={12} /> 匯入
        </button>
        <button
          type="button"
          onClick={exportHoldings}
          className="inline-flex items-center gap-1 rounded border border-(--border) px-2 py-1 text-[11px] text-(--text-secondary) hover:border-(--accent) hover:text-(--accent)"
        >
          <Download size={12} /> 匯出
        </button>
        <span className="text-[10px] text-(--text-muted)">既有持股加進來會交給企鵝管理、出現在下方計畫卡</span>
      </div>

      {adding && <AddHoldingForm onSubmit={submitAddHolding} onCancel={() => setAdding(false)} busy={importHoldings.isPending} />}

      {importText !== null && (
        <div className="mb-3 rounded border border-(--border) bg-(--bg-base) p-2">
          <textarea
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            placeholder='貼上 portfolio.json，例如 [{"symbol":"2330.TW","market":"TW","qty":1000,"costBasis":900,"buyDate":"2024-01-15"}]'
            className="h-24 w-full rounded border border-(--border) bg-(--bg-card) p-1.5 font-mono text-[11px] text-(--text-primary)"
          />
          <div className="mt-1 flex gap-1">
            <button type="button" onClick={applyImportText} disabled={importHoldings.isPending} className="h-7 rounded bg-(--accent) px-2 text-[11px] font-semibold text-black disabled:opacity-60">匯入並接手</button>
            <button type="button" onClick={() => setImportText(null)} className="h-7 rounded border border-(--border) px-2 text-[11px] text-(--text-secondary)">取消</button>
          </div>
        </div>
      )}

      {ioMsg && (
        <div className="mb-3 rounded border border-(--accent)/30 bg-(--accent)/10 px-3 py-2 text-[11px] text-(--text-secondary)">{ioMsg}</div>
      )}

      <div className="grid gap-2 md:grid-cols-4 mb-3">
        <Summary label="目前列表" value={`${rows.length} 筆`} />
        <Summary label="進行中" value={`${stats.open} 筆`} />
        <Summary
          label="未實現損益（含手續費）"
          value={moneyByCurrency(stats.unrealUSD, stats.unrealTWD, stats.unrealUsdSeen, stats.unrealTwdSeen)}
          sub={usdTwd != null && stats.unrealUsdSeen
            ? `合計 ≈ TWD ${(stats.unrealUSD * usdTwd + stats.unrealTWD).toLocaleString(undefined, { maximumFractionDigits: 0 })}（匯率 ${usdTwd.toFixed(3)}）`
            : undefined}
          accent={unrealTotal > 0 ? 'up' : unrealTotal < 0 ? 'down' : 'flat'}
        />
        <Summary
          label="已實現損益"
          value={moneyByCurrency(stats.realizedUSD, stats.realizedTWD, stats.realizedUsdSeen, stats.realizedTwdSeen)}
          sub={usdTwd != null && stats.realizedUsdSeen
            ? `合計 ≈ TWD ${(stats.realizedUSD * usdTwd + stats.realizedTWD).toLocaleString(undefined, { maximumFractionDigits: 0 })}（匯率 ${usdTwd.toFixed(3)}）`
            : undefined}
          accent={realizedTotal > 0 ? 'up' : realizedTotal < 0 ? 'down' : 'flat'}
        />
      </div>

      {theses.isLoading && <div className="h-24 skeleton" />}
      {theses.error && <div className="rounded border border-(--warn)/40 bg-(--warn)/10 p-3 text-sm text-(--warn)">讀取交易計畫失敗：{(theses.error as Error).message}</div>}
      {!theses.isLoading && !theses.error && rows.length === 0 && (
        <div className="rounded border border-(--border) bg-(--bg-base) p-6 text-center text-sm text-(--text-muted)">
          {tab === 'open' ? '目前沒有進行中的交易計畫。從今日行動採納或把投資組合交給企鵝管理後會出現在這裡。' : '目前沒有已結案的交易計畫。'}
        </div>
      )}

      {!theses.isLoading && !theses.error && marketGroups.map((group) => (
        <div key={group.key} className="mb-3">
          <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-(--text-secondary)">
            {group.label}
            <span className="text-[10px] font-normal text-(--text-muted)">{group.list.length} 筆</span>
          </div>
          {tab === 'open' ? (
            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              {group.list.map((thesis) => (
                <ThesisCard
                  key={thesis.id}
                  thesis={thesis}
                  livePrice={livePrices.get(thesis.symbol)}
                  usdTwd={usdTwd}
                  alerts={latestAlertsForThesis(thesis, recentAlerts)}
                  onSelectSymbol={onSelectSymbol}
                  onClose={() => {
                    if (confirm(`確定手動關閉 ${thesis.symbol} 交易計畫？`)) close.mutate(thesis.id);
                  }}
                  onDelete={() => {
                    if (confirm(`確定彻底刪除 ${thesis.symbol} 交易計畫？這會連同本機持股一起移除，無法復原。`)) del.mutate(thesis);
                  }}
                  onSaved={() => {
                    queryClient.invalidateQueries({ queryKey: ['theses'] });
                    queryClient.invalidateQueries({ queryKey: ['daily-plan'] });
                    queryClient.invalidateQueries({ queryKey: ['managed-theses'] });
                  }}
                  closing={close.isPending}
                  deleting={del.isPending}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-1">
              {group.list.map((thesis) => (
                <ClosedThesisRow
                  key={thesis.id}
                  thesis={thesis}
                  onSelectSymbol={onSelectSymbol}
                  onDelete={() => {
                    if (confirm(`確定彻底刪除 ${thesis.symbol} 已結案計畫？無法復原。`)) del.mutate(thesis);
                  }}
                  deleting={del.isPending}
                  onSaved={() => {
                    queryClient.invalidateQueries({ queryKey: ['theses'] });
                    queryClient.invalidateQueries({ queryKey: ['daily-plan'] });
                    queryClient.invalidateQueries({ queryKey: ['managed-theses'] });
                  }}
                />
              ))}
            </div>
          )}
        </div>
      ))}

      {risk && (
        <div className="mt-3 border-t border-(--border) pt-3">
          <h4 className="mb-2 flex flex-wrap items-center gap-1 text-[11px] font-semibold text-(--text-secondary)">
            <AlertTriangle size={12} className="text-(--warn)" /> 風險指標
            <span className="text-[10px] font-normal text-(--text-muted)">中長線參考（短線可在購買力設定切策略隱藏）</span>
          </h4>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
            <RiskStat label="集中度 HHI" value={risk.hhi.toFixed(3)} hint={risk.hhi > 0.5 ? '⚠ 高集中（>0.5）' : risk.hhi > 0.25 ? '中度集中' : '分散良好'} accent={risk.hhi > 0.5 ? 'down' : risk.hhi > 0.25 ? 'flat' : 'up'} />
            <RiskStat label="持倉檔數" value={String(risk.count)} hint={risk.count < 5 ? '偏集中（建議 ≥5）' : risk.count > 20 ? '過度分散' : '合理'} />
            <RiskStat label="最大佔比" value={`${(risk.maxWeight * 100).toFixed(1)}%`} hint="單檔最高佔比" />
          </div>
          <p className="mt-1.5 text-[10px] leading-relaxed text-(--text-muted)">
            <b>HHI</b> = Σ(權重²)，越接近 1 越集中。跨幣別已用即時匯率正規化到 TWD 比較。⚠ 僅供研究，<b>AI 輔助、扣板機自負</b>。
          </p>
        </div>
      )}
    </section>
  );
}

function ThesisCard({ thesis, livePrice, usdTwd, alerts, onSelectSymbol, onClose, onDelete, onSaved, closing, deleting }: { thesis: Thesis; livePrice?: number; usdTwd?: number; alerts: Alert[]; onSelectSymbol?: (symbol: string) => void; onClose: () => void; onDelete: () => void; onSaved: () => void; closing: boolean; deleting: boolean }) {
  const closed = isClosedStatus(thesis.status);
  const [editing, setEditing] = useState(false);
  const [showFills, setShowFills] = useState(false);
  // 進行中用即時報價（/api/quote），未到時退回後端評估快照；已結案用最後快照。
  const quote = closed ? latestQuote(thesis) : (livePrice ?? latestQuote(thesis));
  const basis = thesis.actualEntry ?? thesis.entry;
  const shares = sharesOf(thesis);
  const hasSold = (thesis.fills ?? []).some((f) => f.side === 'sell');
  const breakEven = thesis.breakEvenPrice;
  // 多筆買進（無賣出）時 stop/T1/T2 以剩餘均價重錨 → 標「均價進」；有賣出時改標「損平」（見下）。
  const levelsOnAvgCost = (thesis.fills?.length ?? 0) > 1 || hasSold;
  const feeModel = feeModelFor(thesis.symbol);
  // 剩餘部位未實現（含來回手續費 + 賣出稅費）；basis = 剩餘真實成本（actualEntry）。
  const rt = !closed && livePrice != null && basis > 0 && shares
    ? roundTripPnl(basis, livePrice, shares, feeModel, feeOpts(thesis))
    : null;
  // 損益金額：有賣出 → 整筆（已實現 FIFO 券商式 + 剩餘部位未實現）；純買進 → 剩餘部位未實現。
  const totalNetPnl = rt ? (hasSold ? (thesis.realizedPnl ?? 0) + rt.netPnl : rt.netPnl) : null;
  // 損益方向 / 百分比：有賣出且有損平價 → 現價相對損平的幅度（一眼看離損平多遠、整筆綠紅）；否則剩餘部位 pct。
  const pnlPct = closed
    ? thesis.finalPnlPct
    : (hasSold && breakEven != null && breakEven > 0 && livePrice != null
        ? (livePrice - breakEven) / breakEven
        : (rt
            ? rt.netPnlPct
            : (livePrice != null && basis > 0 ? netPnlPctNoShares(basis, livePrice, feeModel) ?? undefined : thesis.finalPnlPct)));
  const dir: 'up' | 'down' | null = pnlPct == null ? null : pnlPct >= 0 ? 'up' : 'down';
  const conv = totalNetPnl != null ? convertedMoney(totalNetPnl, currencyFor(thesis.symbol), usdTwd) : null;
  const pnlCls = dir === 'up' ? 'text-(--up)' : dir === 'down' ? 'text-(--down)' : 'text-(--text-primary)';
  const leftBorder = dir === 'up' ? 'border-l-(--up)' : dir === 'down' ? 'border-l-(--down)' : 'border-l-(--border-strong)';
  return (
    <article className={`rounded-lg border border-(--border) border-l-4 ${leftBorder} bg-(--bg-base) px-2.5 py-2`}>
      {alerts.length > 0 && (
        <div className="mb-1.5 space-y-1">
          {alerts.map((alert) => (
            <div key={alert.id} className={`rounded border px-2 py-1 text-[10px] leading-snug ${alertBannerClass(alert.severity)}`}>
              <span className="font-black">{alertLabel(alert.type)}</span>
              <span className="ml-1 text-(--text-primary)">{alert.body}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-1.5 flex-wrap">
        <button type="button" onClick={() => onSelectSymbol?.(thesis.symbol)} className="font-black text-[13px] text-(--accent) hover:underline">
          {thesis.symbol}
        </button>
        <span className={`rounded border px-1.5 py-0.5 text-[10px] font-bold ${closed ? 'border-(--text-muted) text-(--text-muted)' : 'border-(--up)/50 text-(--up)'}`}>
          {statusLabel(thesis.status)}
        </span>
        {thesis.source && <span className="rounded border border-(--border) px-1.5 py-0.5 text-[10px] text-(--text-muted)">{thesisSourceLabel(thesis.source)}</span>}
        {!closed && livePrice != null && <span className="text-[9px] font-bold text-(--accent)">即時</span>}
        <span className={`ml-auto text-[13px] font-black tnum ${dir == null ? 'text-(--text-primary)' : dir === 'up' ? 'text-(--up)' : 'text-(--down)'}`}>
          {pct(pnlPct)}
        </span>
      </div>

      <PriceTrack stop={thesis.stop} entry={thesis.entry} t1={thesis.t1} t2={thesis.t2} price={quote} />

      <div className="mt-2 grid grid-cols-4 gap-1">
        <LevelCell
          label={hasSold ? '損平' : (levelsOnAvgCost ? '均價進' : '進場')}
          value={price(hasSold && breakEven != null ? breakEven : thesis.entry)}
          title={hasSold
            ? '部分賣出後已把已實現損益攤進剩餘部位＝賣到此價整筆口袋打平（含買進費 + 賣出費）；停損 / T1 / T2 以損平價重算'
            : (levelsOnAvgCost ? '多筆成交後，停損 / T1 / T2 已以剩餘部位均價用 L1（ATR + 支撐壓力）重算' : undefined)}
        />
        <LevelCell label="停損" value={price(thesis.stop)} tone="down" />
        <LevelCell label="T1" value={price(thesis.t1)} tone="up" />
        <LevelCell label="T2" value={price(thesis.t2)} tone="up" />
      </div>

      <div className="mt-1.5 flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 text-[10px] tnum">
        {shares != null && (
          <span className="text-(--text-muted)">持有 <b className="text-(--text-secondary)">{price(shares)}</b> 股
            {basis > 0 && <> · 均價 <b className="text-(--text-secondary)">{price(basis)}</b></>}
          </span>
        )}
        <span className="flex flex-wrap items-center gap-x-2">
          {totalNetPnl != null && (
            <span>
              <span className="text-(--text-muted)">{hasSold ? '損益(整筆) ' : '損益 '}</span>
              <b className={pnlCls}>{money(totalNetPnl, thesis.symbol)}</b>
              {conv && <span className="text-(--text-muted)"> {conv}</span>}
            </span>
          )}
          {closed && thesis.realizedPnl != null && (
            <span><span className="text-(--text-muted)">已實現 </span><b className={thesis.realizedPnl >= 0 ? 'text-(--up)' : 'text-(--down)'}>{money(thesis.realizedPnl, thesis.symbol)}</b></span>
          )}
        </span>
      </div>

      <ReasonSignals reasons={thesis.reasonsTop3} />

      <div className="mt-1.5 flex items-center justify-between gap-2">
        <div className="min-w-0 truncate text-[9px] text-(--text-muted)">
          {closed ? (
            `關閉：${thesis.finalReason ?? statusLabel(thesis.status)}`
          ) : (
            <>
              採納 {new Date(thesis.adoptedAt).toLocaleDateString('zh-TW')} ·{' '}
              <button
                type="button"
                onClick={() => setShowFills((v) => !v)}
                className="underline decoration-dotted underline-offset-2 hover:text-(--accent)"
                title="展開成交明細，可逐筆改價 / 量 / 刪除"
              >
                成交 {thesis.fills?.length ?? 0} 筆
              </button>
            </>
          )}
        </div>
        {!closed && (
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => setEditing((v) => !v)}
              className="inline-flex items-center gap-1 rounded border border-(--border) px-1.5 py-0.5 text-[10px] text-(--text-secondary) hover:border-(--accent) hover:text-(--accent)"
            >
              <Pencil size={11} /> 編輯
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={closing}
              className="inline-flex items-center gap-1 rounded border border-(--down)/50 px-1.5 py-0.5 text-[10px] text-(--down) hover:bg-(--down)/10 disabled:opacity-60"
            >
              <XCircle size={11} /> 關閉
            </button>
            <button
              type="button"
              onClick={onDelete}
              disabled={deleting}
              aria-label="刪除"
              title="彻底刪除（誤建用）"
              className="inline-flex items-center rounded border border-(--border) p-0.5 text-(--text-muted) hover:border-(--down) hover:text-(--down) disabled:opacity-60"
            >
              <Trash2 size={11} />
            </button>
          </div>
        )}
      </div>

      {editing && !closed && (
        <ThesisEditForm thesis={thesis} onCancel={() => setEditing(false)} onSaved={onSaved} />
      )}
      {showFills && !closed && (
        <FillsEditor thesis={thesis} onCancel={() => setShowFills(false)} onSaved={onSaved} />
      )}
    </article>
  );
}

/**
 * 價位軌道：把現價畫在「停損 → 進場 → 目標」的相對位置上，取代看不懂的純白進度條。
 * 台股紅綠：進場價左側（虧損區）淡綠、右側（獲利區）淡紅；進場有分界線、T1 有刻度、現價是游標。
 */
function PriceTrack({ stop, entry, t1, t2, price: cur }: { stop: number; entry: number; t1: number; t2: number; price?: number }) {
  const pts = [stop, entry, t1, t2].filter((n): n is number => typeof n === 'number' && Number.isFinite(n));
  if (pts.length < 2) return null;
  const lo = Math.min(...pts);
  const hi = Math.max(...pts);
  const span = hi - lo;
  if (!(span > 0)) return null;
  const posOf = (v: number) => Math.max(0, Math.min(100, ((v - lo) / span) * 100));
  const entryPos = posOf(entry);
  const t1Pos = posOf(t1);
  const hasCur = typeof cur === 'number' && Number.isFinite(cur);
  const curPos = hasCur ? posOf(cur as number) : null;
  const belowEntry = hasCur && (cur as number) < entry;
  const curTone = belowEntry ? 'bg-(--down)' : 'bg-(--up)';
  return (
    <div className="mt-2 select-none" aria-label="價位軌道（停損→進場→目標）">
      <div className="relative h-3.5">
        {curPos != null && (
          <div className="absolute top-0 -translate-x-1/2" style={{ left: `${curPos}%` }}>
            <span className={`whitespace-nowrap rounded px-1 text-[9px] font-bold tnum text-white ${curTone}`}>現價 {price(cur)}</span>
          </div>
        )}
      </div>
      <div
        className="relative h-2 rounded-full border border-(--border)"
        style={{
          background: `linear-gradient(90deg, color-mix(in srgb, var(--down) 50%, transparent) 0%, color-mix(in srgb, var(--down) 24%, transparent) ${entryPos}%, color-mix(in srgb, var(--up) 24%, transparent) ${entryPos}%, color-mix(in srgb, var(--up) 50%, transparent) 100%)`,
        }}
      >
        <span className="absolute -top-0.5 -bottom-0.5 w-px bg-(--text-secondary)" style={{ left: `${entryPos}%` }} aria-hidden />
        <span className="absolute top-0 bottom-0 w-px bg-(--up)/50" style={{ left: `${t1Pos}%` }} aria-hidden />
        {curPos != null && (
          <span className={`absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-sm ${curTone}`} style={{ left: `${curPos}%` }} aria-hidden />
        )}
      </div>
      <div className="relative mt-1 h-3 text-[9px] tnum text-(--text-muted)">
        <span className="absolute left-0">停損</span>
        <span className="absolute -translate-x-1/2 font-semibold text-(--text-secondary)" style={{ left: `${entryPos}%` }}>進場</span>
        <span className="absolute right-0">目標</span>
      </div>
    </div>
  );
}

/** 單一價位格（進場 / 停損 / T1 / T2）：取代密集的純文字行。 */
function LevelCell({ label, value, tone = 'base', title }: { label: string; value: string; tone?: 'base' | 'up' | 'down'; title?: string }) {
  const cls = tone === 'up' ? 'text-(--up)' : tone === 'down' ? 'text-(--down)' : 'text-(--text-primary)';
  return (
    <div className="rounded border border-(--border) bg-(--bg-card) px-1 py-0.5 text-center" title={title}>
      <div className="text-[9px] text-(--text-muted)">{label}</div>
      <div className={`text-[11px] font-bold tnum ${cls}`}>{value}</div>
    </div>
  );
}

/**
 * 把 reasonsTop3 漂亮化：facts-only 的 `L1 <factor>：±N (note)` 解析成彩色 chip（▲/▼ + 中文標 + 分數，
 * hover 看完整 note）；非此格式（LLM 句子）則維持一行帶腳印 emoji 的句子。
 */
function ReasonSignals({ reasons }: { reasons?: string[] }) {
  if (!reasons || reasons.length === 0) return null;
  const parsed = reasons.map((r) => parseL1Reason(r));
  if (parsed[0] == null) {
    return <div className="mt-1.5 text-[10px] leading-snug text-(--text-secondary)">🐾 {reasons[0]}</div>;
  }
  const chips = parsed.filter((p): p is NonNullable<typeof p> => p != null).slice(0, 4);
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1">
      <span className="text-[9px] font-bold text-(--text-muted)">L1 訊號</span>
      {chips.map((c, i) => {
        const flat = c.contribution === 0;
        const up = c.contribution > 0;
        const tone = flat
          ? 'border-(--border) text-(--text-muted)'
          : up
            ? 'border-(--up)/50 bg-(--up)/6 text-(--up)'
            : 'border-(--down)/50 bg-(--down)/6 text-(--down)';
        return (
          <span key={i} title={c.note || undefined} className={`inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold ${tone}`}>
            {flat ? '•' : up ? '▲' : '▼'} {c.label}
            <b className="tnum">{c.contribution > 0 ? '+' : ''}{c.contribution}</b>
          </span>
        );
      })}
    </div>
  );
}

function numToStr(n: number | undefined): string {
  return typeof n === 'number' && Number.isFinite(n) ? String(n) : '';
}

function EditField({ label, value, onChange, step = '0.01' }: { label: string; value: string; onChange: (v: string) => void; step?: string }) {
  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-[9px] text-(--text-muted)">{label}</span>
      <input
        type="number"
        step={step}
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-7 rounded border border-(--border) bg-(--bg-base) px-1.5 text-[11px] tnum"
      />
    </label>
  );
}

/** ISO 時間 → date input 值（本地 yyyy-mm-dd）。 */
function dateInputValue(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

interface EditableFill {
  id: string;
  side: 'buy' | 'sell';
  price: string;
  quantity: string;
  dateStr: string;
  origFilledAt?: string;
  /** 原始手填手續費（UI 不編輯，存回時保留，避免被自動預估覆蓋） */
  fees?: number;
}

/**
 * 成交明細編輯：逐筆改買/賣價、量、日期，可刪可增，整組存回 /api/thesis/{id}/fills。
 * 後端走 FIFO reconcile 重算剩餘部位 / 已實現損益。給「打錯字」「補登漏記的買賣」用。
 * 一張卡（一檔）內就能看到並修正每一筆買進 / 賣出，不再「多筆成交不能編輯」。
 */
function FillsEditor({ thesis, onCancel, onSaved }: { thesis: Thesis; onCancel: () => void; onSaved: () => void }) {
  const qc = useQueryClient();
  const [fills, setFills] = useState<EditableFill[]>(() =>
    (thesis.fills ?? []).map((f) => ({
      id: f.id,
      side: f.side,
      price: numToStr(f.price),
      quantity: numToStr(f.quantity),
      dateStr: dateInputValue(f.filledAt),
      origFilledAt: f.filledAt,
      fees: f.fees,
    })),
  );
  const [error, setError] = useState<string | null>(null);

  const update = (i: number, patch: Partial<EditableFill>) =>
    setFills((prev) => prev.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const remove = (i: number) => setFills((prev) => prev.filter((_, idx) => idx !== i));
  const add = (side: 'buy' | 'sell') =>
    setFills((prev) => [...prev, { id: '', side, price: '', quantity: '', dateStr: dateInputValue(new Date().toISOString()) }]);

  // 即時加總（非 FIFO 損益，只給「剩餘股數對不對」的即時感；損益由後端存完更新卡片）。
  const buyQty = fills.filter((f) => f.side === 'buy').reduce((s, f) => s + (Number(f.quantity) || 0), 0);
  const sellQty = fills.filter((f) => f.side === 'sell').reduce((s, f) => s + (Number(f.quantity) || 0), 0);
  const remaining = Math.round((buyQty - sellQty) * 1000) / 1000;

  const save = useMutation({
    mutationFn: () => {
      const payload = fills.map((f) => {
        const price = Number(f.price);
        const quantity = Number(f.quantity);
        if (!Number.isFinite(price) || price <= 0) throw new Error('每筆成交價需 > 0');
        if (!Number.isFinite(quantity) || quantity <= 0) throw new Error('每筆股數需 > 0');
        const filledAt = f.origFilledAt && dateInputValue(f.origFilledAt) === f.dateStr
          ? f.origFilledAt
          : (f.dateStr ? new Date(`${f.dateStr}T12:00:00`).toISOString() : undefined);
        return {
          ...(f.id ? { id: f.id } : {}),
          side: f.side,
          price,
          quantity,
          ...(filledAt ? { filledAt } : {}),
          ...(f.fees != null ? { fees: f.fees } : {}),
        };
      });
      return fetchJSON<Thesis>(`/api/thesis/${encodeURIComponent(thesis.id)}/fills`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fills: payload }),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['theses'] });
      qc.invalidateQueries({ queryKey: ['daily-plan'] });
      qc.invalidateQueries({ queryKey: ['managed-theses'] });
      onSaved();
      onCancel();
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  return (
    <div className="mt-2 rounded border border-(--accent)/30 bg-(--bg-card) p-2">
      <div className="mb-1.5 text-[10px] font-semibold text-(--accent)">📝 成交明細（改錯字 / 加減筆）</div>
      <div className="space-y-1">
        {fills.length === 0 && <div className="text-[10px] text-(--text-muted)">尚無成交，按下方「+ 買進」新增。</div>}
        {fills.map((f, i) => (
          <div key={f.id || `new-${i}`} className="flex items-center gap-1">
            <select
              value={f.side}
              onChange={(e) => update(i, { side: e.target.value as 'buy' | 'sell' })}
              className={`h-7 shrink-0 rounded border px-1 text-[11px] font-semibold ${f.side === 'buy' ? 'border-(--up)/50 text-(--up)' : 'border-(--down)/50 text-(--down)'} bg-(--bg-base)`}
              aria-label="買賣方向"
            >
              <option value="buy">買</option>
              <option value="sell">賣</option>
            </select>
            <input type="number" step="0.01" min="0" value={f.price} onChange={(e) => update(i, { price: e.target.value })}
              placeholder="成交價" aria-label="成交價" className="h-7 w-16 rounded border border-(--border) bg-(--bg-base) px-1 text-[11px] tnum" />
            <input type="number" step="0.001" min="0" value={f.quantity} onChange={(e) => update(i, { quantity: e.target.value })}
              placeholder="股數" aria-label="股數" className="h-7 w-14 rounded border border-(--border) bg-(--bg-base) px-1 text-[11px] tnum" />
            <input type="date" value={f.dateStr} onChange={(e) => update(i, { dateStr: e.target.value })}
              aria-label="成交日期" className="h-7 min-w-0 flex-1 rounded border border-(--border) bg-(--bg-base) px-1 text-[10px] tnum" />
            <button type="button" onClick={() => remove(i)} aria-label="刪除這筆成交" title="刪除這筆"
              className="shrink-0 rounded border border-(--border) p-1 text-(--text-muted) hover:border-(--down) hover:text-(--down)">
              <Trash2 size={11} />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <button type="button" onClick={() => add('buy')} className="rounded border border-(--up)/50 px-1.5 py-0.5 text-[10px] font-semibold text-(--up) hover:bg-(--up)/10">+ 買進</button>
        <button type="button" onClick={() => add('sell')} className="rounded border border-(--down)/50 px-1.5 py-0.5 text-[10px] font-semibold text-(--down) hover:bg-(--down)/10">+ 賣出</button>
        <span className="ml-auto text-[10px] tnum text-(--text-muted)">買 {buyQty} · 賣 {sellQty} · 剩 <b className={remaining < 0 ? 'text-(--down)' : 'text-(--text-secondary)'}>{remaining}</b> 股</span>
      </div>
      <div className="mt-1 text-[9px] text-(--text-muted)">手續費未填時自動依國泰費率預估（買賣都算）。移動加權平均：賣出用當下整體持倉均價算損益。</div>
      {error && <div className="mt-1.5 text-[10px] text-(--down)">{error}</div>}
      <div className="mt-2 flex items-center gap-1.5">
        <button type="button" onClick={() => save.mutate()} disabled={save.isPending}
          className="inline-flex items-center gap-1 rounded bg-(--accent) px-2 py-1 text-[10px] font-semibold text-black disabled:opacity-60">
          <Check size={11} /> {save.isPending ? '儲存中' : '儲存明細'}
        </button>
        <button type="button" onClick={onCancel} className="rounded border border-(--border) px-2 py-1 text-[10px] text-(--text-secondary)">取消</button>
      </div>
    </div>
  );
}

/** 修正交易計畫：進行中改計畫價位 + 單純部位成本 / 股數；已結案改實際進 / 出場 / 股數，存回 /api/thesis/{id}/update。 */
function ThesisEditForm({ thesis, onCancel, onSaved }: { thesis: Thesis; onCancel: () => void; onSaved: () => void }) {
  const closed = isClosedStatus(thesis.status);
  const fills = thesis.fills ?? [];
  // 只有「買進 ≤ 1 筆且賣出 ≤ 1 筆」的單純部位才開放改成本 / 股數 / 出場；多筆成交走買賣回填，避免破壞已實現損益。
  const canEditPosition = fills.filter((f) => f.side === 'sell').length <= 1 && fills.filter((f) => f.side === 'buy').length <= 1;
  const [entry, setEntry] = useState(numToStr(thesis.entry));
  const [stop, setStop] = useState(numToStr(thesis.stop));
  const [t1, setT1] = useState(numToStr(thesis.t1));
  const [t2, setT2] = useState(numToStr(thesis.t2));
  const [actualEntry, setActualEntry] = useState(numToStr(thesis.actualEntry));
  const [actualExit, setActualExit] = useState(numToStr(thesis.actualExit));
  // 已結案多半已全數賣出（remainingShares=0），股數預設用進場總量 filledShares；進行中用目前持有 remainingShares。
  const [shares, setShares] = useState(numToStr(
    closed
      ? (thesis.filledShares ?? thesis.sharesPlanned ?? thesis.remainingShares)
      : (thesis.remainingShares ?? thesis.filledShares ?? thesis.sharesPlanned),
  ));
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => fetchJSON<Thesis>(`/api/thesis/${encodeURIComponent(thesis.id)}/update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(
        closed
          ? { actualEntry, actualExit, shares }
          : { entry, stop, t1, t2, ...(canEditPosition ? { actualEntry, shares } : {}) },
      ),
    }),
    onSuccess: () => { onSaved(); onCancel(); },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });
  return (
    <div className="mt-2 rounded border border-(--accent)/30 bg-(--bg-card) p-2">
      <div className="mb-1.5 text-[10px] font-semibold text-(--accent)">✏️ 修正{closed ? '紀錄' : '計畫'}（打錯字可改）</div>
      {closed ? (
        canEditPosition ? (
          <div className="grid grid-cols-3 gap-1.5">
            <EditField label="實際進場" value={actualEntry} onChange={setActualEntry} />
            <EditField label="實際出場" value={actualExit} onChange={setActualExit} />
            <EditField label="股數" value={shares} onChange={setShares} step="0.001" />
          </div>
        ) : (
          <div className="text-[9px] text-(--text-muted)">已有多筆成交，進 / 出場 / 數量請用「買賣回填」調整。</div>
        )
      ) : (
        <>
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
            <EditField label="進場價" value={entry} onChange={setEntry} />
            <EditField label="停損" value={stop} onChange={setStop} />
            <EditField label="T1" value={t1} onChange={setT1} />
            <EditField label="T2" value={t2} onChange={setT2} />
          </div>
          {canEditPosition ? (
            <div className="mt-1.5 grid grid-cols-2 gap-1.5">
              <EditField label="實際成本" value={actualEntry} onChange={setActualEntry} />
              <EditField label="持有股數" value={shares} onChange={setShares} step="0.001" />
            </div>
          ) : (
            <div className="mt-1.5 text-[9px] text-(--text-muted)">已有多筆成交，數量 / 成本請用「買賣回填」調整。</div>
          )}
        </>
      )}
      {error && <div className="mt-1.5 text-[10px] text-(--down)">{error}</div>}
      <div className="mt-2 flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="inline-flex items-center gap-1 rounded bg-(--accent) px-2 py-1 text-[10px] font-semibold text-black disabled:opacity-60"
        >
          <Check size={11} /> {save.isPending ? '儲存中' : '儲存'}
        </button>
        <button type="button" onClick={onCancel} className="rounded border border-(--border) px-2 py-1 text-[10px] text-(--text-secondary)">取消</button>
      </div>
    </div>
  );
}

/** 已結案交易計畫：一筆一排精簡列，可展開編輯（修正實際進 / 出場 / 股數）或刪除（誤建）。 */
function ClosedThesisRow({ thesis, onSelectSymbol, onSaved, onDelete, deleting }: { thesis: Thesis; onSelectSymbol?: (symbol: string) => void; onSaved: () => void; onDelete: () => void; deleting: boolean }) {
  const [editing, setEditing] = useState(false);
  const fills = thesis.fills ?? [];
  const pnlPct = thesis.realizedPnlPct ?? thesis.finalPnlPct;
  const dir: 'up' | 'down' | null = pnlPct == null ? null : pnlPct >= 0 ? 'up' : 'down';
  const pnlCls = dir === 'up' ? 'text-(--up)' : dir === 'down' ? 'text-(--down)' : 'text-(--text-muted)';
  return (
    <div className="rounded border border-(--border) bg-(--bg-base)">
      <div className="flex items-center gap-2 px-2.5 py-1.5 text-[11px]">
        <button type="button" onClick={() => onSelectSymbol?.(thesis.symbol)} className="w-16 shrink-0 text-left font-bold text-(--accent) hover:underline">{thesis.symbol}</button>
        <span className="shrink-0 rounded border border-(--text-muted) px-1 py-0.5 text-[9px] text-(--text-muted)">{statusLabel(thesis.status)}</span>
        <span className="hidden tnum text-(--text-muted) sm:inline">進 {price(thesis.actualEntry ?? thesis.entry)} → 出 {price(thesis.actualExit)}{fills.length > 1 ? ` · 成交 ${fills.length} 筆` : ''}</span>
        <span className={`ml-auto tnum ${thesis.realizedPnl != null ? pnlCls : 'text-(--text-muted)'}`}>{thesis.realizedPnl != null ? money(thesis.realizedPnl, thesis.symbol) : '—'}</span>
        <span className={`w-16 text-right font-bold tnum ${pnlCls}`}>{pct(pnlPct)}</span>
        <span className="hidden w-20 shrink-0 text-right text-[9px] text-(--text-muted) md:inline">{thesis.closedAt ? new Date(thesis.closedAt).toLocaleDateString('zh-TW') : ''}</span>
        <button
          type="button"
          onClick={() => setEditing((v) => !v)}
          aria-label="看 / 改成交明細"
          title={fills.length > 0 ? '看 / 改成交明細（逐筆買賣）' : '編輯'}
          className="shrink-0 rounded border border-(--border) p-0.5 text-(--text-muted) hover:border-(--accent) hover:text-(--accent)"
        >
          <Pencil size={11} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={deleting}
          aria-label="刪除"
          title="彻底刪除（誤建用）"
          className="shrink-0 rounded border border-(--border) p-0.5 text-(--text-muted) hover:border-(--down) hover:text-(--down) disabled:opacity-60"
        >
          <Trash2 size={11} />
        </button>
      </div>
      {editing && (
        <div className="px-2.5 pb-2">
          {fills.length > 0
            ? <FillsEditor thesis={thesis} onCancel={() => setEditing(false)} onSaved={onSaved} />
            : <ThesisEditForm thesis={thesis} onCancel={() => setEditing(false)} onSaved={onSaved} />}
        </div>
      )}
    </div>
  );
}

function Summary({ label, value, sub, accent = 'flat' }: { label: string; value: string; sub?: string; accent?: 'up' | 'down' | 'flat' }) {
  const cls = accent === 'up' ? 'text-(--up)' : accent === 'down' ? 'text-(--down)' : 'text-(--text-primary)';
  return (
    <div className="rounded border border-(--border) bg-(--bg-base) px-2 py-1.5">
      <div className="text-[10px] text-(--text-muted)">{label}</div>
      <div className={`text-sm font-bold tnum ${cls}`}>{value}</div>
      {sub && <div className="text-[10px] text-(--text-muted) tnum">{sub}</div>}
    </div>
  );
}

function RiskStat({ label, value, hint, accent = 'flat' }: { label: string; value: string; hint?: string; accent?: 'up' | 'down' | 'flat' }) {
  const cls = accent === 'up' ? 'text-(--up)' : accent === 'down' ? 'text-(--down)' : 'text-(--text-primary)';
  return (
    <div className="rounded border border-(--border) bg-(--bg-base) p-2">
      <div className="text-[10px] text-(--text-muted)">{label}</div>
      <div className={`text-sm font-bold tnum ${cls}`}>{value}</div>
      {hint && <div className="text-[10px] text-(--text-muted)">{hint}</div>}
    </div>
  );
}

function AddHoldingForm({ onSubmit, onCancel, busy }: { onSubmit: (p: Omit<Position, 'id'>) => void; onCancel: () => void; busy?: boolean }) {
  const [symbol, setSymbol] = useState('');
  const [qty, setQty] = useState('');
  const [costBasis, setCostBasis] = useState('');
  const [buyDate, setBuyDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [resolving, setResolving] = useState(false);
  const pending = busy || resolving;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (pending) return;
    const q = parseFloat(qty);
    const c = parseFloat(costBasis);
    if (!symbol.trim() || isNaN(q) || q <= 0 || isNaN(c) || c <= 0) {
      alert('請填入有效的代號 / 股數 / 成本');
      return;
    }
    setResolving(true);
    try {
      // 市場與上市櫃（.TW / .TWO）一律依代號由後端官方表自動判斷。
      const resolved = await resolveSymbolInput(symbol);
      if (!resolved) {
        alert('無法辨識代號，請確認輸入');
        return;
      }
      onSubmit({ symbol: resolved.symbol, market: resolved.market, qty: q, costBasis: c, buyDate, note: note.trim() || undefined });
    } finally {
      setResolving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mb-3 rounded border border-(--border) bg-(--bg-base) p-2">
      <div className="grid grid-cols-2 gap-1.5 md:grid-cols-5">
        <input type="text" required placeholder="代號 e.g. 2330 / 6488 / 台積電 / MSFT" value={symbol} onChange={(e) => setSymbol(e.target.value)} className="col-span-2 h-8 rounded border border-(--border) bg-(--bg-card) px-2 text-[11px]" />
        <input type="number" required step="0.001" min="0" placeholder="股數" value={qty} onChange={(e) => setQty(e.target.value)} className="h-8 rounded border border-(--border) bg-(--bg-card) px-2 text-[11px]" />
        <input type="number" required step="0.01" min="0" placeholder="成本價" value={costBasis} onChange={(e) => setCostBasis(e.target.value)} className="h-8 rounded border border-(--border) bg-(--bg-card) px-2 text-[11px]" />
        <input type="date" aria-label="買入日期" value={buyDate} onChange={(e) => setBuyDate(e.target.value)} className="h-8 rounded border border-(--border) bg-(--bg-card) px-2 text-[11px]" />
        <input type="text" placeholder="備註（可空）" value={note} onChange={(e) => setNote(e.target.value)} className="col-span-2 h-8 rounded border border-(--border) bg-(--bg-card) px-2 text-[11px] md:col-span-4" />
        <div className="col-span-2 flex gap-1 md:col-span-1">
          <button type="submit" disabled={pending} className="h-8 flex-1 rounded bg-(--accent) text-[11px] font-semibold text-black disabled:opacity-60">{pending ? '處理中' : '加入'}</button>
          <button type="button" onClick={onCancel} aria-label="取消" title="取消" className="h-8 rounded border border-(--border) px-2 text-[11px] text-(--text-secondary)"><X className="h-3 w-3" /></button>
        </div>
      </div>
      <div className="mt-1 text-[10px] text-(--text-muted)">市場與上市櫃（.TW / .TWO）會依代號自動判斷，不用選</div>
    </form>
  );
}